import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../config/URL";
import fetchAllCentersWithIds from "../../List/CenterList";
import GlobalDelete from "../../../components/common/GlobalDelete";
import { exportVisibleColumnsToCSV } from "../../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";

const MODULE_KEY = "HOLIDAY";

const Holiday = ({ selectedCenter }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("userName");
  const [loading, setLoading] = useState(true);
  const [centerData, setCenterData] = useState(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const canIndex = Boolean(storedScreens?.holidayIndex);
  const canRead = Boolean(storedScreens?.holidayRead);
  const canCreate = Boolean(storedScreens?.holidayCreate);
  const canUpdate = Boolean(storedScreens?.holidayUpdate);
  const canDelete = Boolean(storedScreens?.holidayDelete);
  const showActionColumn = canUpdate || canDelete;

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const defaultColumnVisibility = {
    holidayDescription: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );
  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "centerName",
        header: "Center Name",
      },
      {
        accessorKey: "holidayName",
        header: "Holiday Name",
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
      },
      { accessorKey: "endDate", header: "End Date" },
      { accessorKey: "holidayDescription", header: "Holiday Description" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    // ✅ Action column ALWAYS second
    return [baseColumns[0], actionColumn, ...baseColumns.slice(1)];
  }, [showActionColumn]);

  /* -------------------- API CALLS -------------------- */
  const fetchData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      setCenterData(centerData);
    } catch (error) {
      toast.error(error);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      if (role !== "SMS_ADMIN") {
        const response = await api.get(`/getAllHolidayListByUserId/${userId}`);
        setData(response.data);
      } else {
        const url =
          selectedCenter === 0 || selectedCenter === "0"
            ? `getAllUserHoliday`
            : `/getAllUserHoliday?centerId=${selectedCenter}`;

        const response = await api.get(url);
        setData(response.data);
      }
    } catch (error) {
      toast.error("Error Fetching Data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    fetchData();
  }, [selectedCenter]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "HolidayList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead
        ? () => navigate(`/holiday/list/${row.original.id}`)
        : undefined,
      style: { cursor: canRead ? "pointer" : "default" },
    }),
  });

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Staffing
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Holiday
        </li>
      </ol>
      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Holiday
              </span>
            </span>
          </div>
        </div>
        <div className="mb-3 d-flex justify-content-end">
          {canCreate && (
            <Link to="/holiday/add">
              <button
                type="button"
                className="btn btn-button btn-sm me-2"
                style={{ fontWeight: "600px !important" }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>
            </Link>
          )}
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {canUpdate && (
                <MenuItem
                  onClick={() => navigate(`/holiday/edit/${selectedId}`)}
                  className="text-start mb-0 menuitem-style"
                >
                  Edit
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteUserHoliday/${selectedId}`}
                    onDeleteSuccess={getData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Holiday;
